import { createAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import { AreaUnit, ContentViewMode, DistanceUnit, ID, SupportedRegions } from "@nova/constants"
import { memoize } from "nodash"
import { OrganizationBasic, OrganizationExtended, TagExtended, TagTree } from "@nova/schemas"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { createAppAsyncThunk } from "integrations/redux/hooks"
import { DateFormat, TimeFormat } from "@nova/format"
import { createFire, updateFire } from "features/entity-caching/fires/thunks"
import { loadAndZoomProject } from "features/map/control/thunks"
import { RootState } from "integrations/redux"
import { getLocalStorageValue } from "tools/store/localStorageRegistry"

const name = "workspace-management-v1"

export type WorkspaceTreeItem = TagExtended

type SliceState = {
    workspaces: OrganizationBasic[]
    tags: TagExtended[]
    activeWorkspace?: OrganizationExtended
    activeTag?: TagExtended
    filteredTagIds: ID[]
    newestTagId?: string
    searchContents?: string
    isSearchingGlobally: boolean
    idRenaming?: ID
    activatingProjectId?: ID
    activeFlyoutMenuId?: ID
    dropdownsOpen: Record<string, boolean>
    isCreatingProject: boolean
    newestProjectId?: ID
}

// const root = (s: RootState) => s[name]

export const {
    selectWorkspaceTree,
    selectActiveWorkspace,
    selectActiveWorkspaceId,
    selectFilteredTagIds,
    selectNewestTagId,
    selectTagTree,
    selectAllWorkspaces,
    selectSearchContents,
    selectIsSearchingGlobally,
    selectIdRenaming,
    selectActivatingProjectId,
    selectActiveFlyoutMenuId,
    selectDropdownsOpen,
    selectIsCreatingProject,
    selectNewestProjectId,
    selectWorkspaceRegion,
} = withRoot((r: Record<typeof name, SliceState>) => r[name], {
    selectWorkspaceTree: () => (s) => s.activeWorkspace?.tagTree,
    selectActiveWorkspace: () => (s) => s.activeWorkspace,
    selectActiveWorkspaceId: () => (s) => s.activeWorkspace?.id,
    selectFilteredTagIds: () => (s) => s.filteredTagIds,
    selectNewestTagId: () => (s) => s.newestTagId,
    selectTagTree: () => (s) => s.activeWorkspace?.tagTree,
    selectAllWorkspaces: () => (s) => s.workspaces,
    selectSearchContents: () => (s) => s.searchContents,
    selectIsSearchingGlobally: () => (s) => s.isSearchingGlobally,

    selectIdRenaming: () => (s) => s.idRenaming,
    selectActivatingProjectId: () => (s) => s.activatingProjectId,
    selectActiveFlyoutMenuId: () => (s) => s.activeFlyoutMenuId,
    selectDropdownsOpen: () => (s) => s.dropdownsOpen,
    selectIsCreatingProject: () => (s) => s.isCreatingProject,
    selectNewestProjectId: () => (s) => s.newestProjectId,
    selectWorkspaceRegion: () => (s) => s.activeWorkspace?.region,
})

export const selectTags = (state: RootState) => state[name].tags

const selectFilteredTags = createSelector([selectFilteredTagIds(), selectTags], (tagIds, tags) => {
    return tagIds.flatMap((id) => {
        const tag = tags.find((tag) => tag.id === id)
        return tag ? [tag] : []
    })
})

const selectTagById = memoize((id: ID) =>
    createSelector([selectTags], (tags) => {
        return tags.find((tag) => tag.id === id)
    })
)

export const selectLegacyWorkspaceTree = createSelector(
    [selectActiveWorkspace(), selectTags],
    (workspace, tags) => {
        if (workspace?.tagTree) return buildNestedTagTree(workspace.tagTree, tags)
    }
)

const setActiveTagAction = createAction<string | undefined>(`${name}/setActiveTagId`)

const setFilteredTagIds = createAction<string[] | undefined>(`${name}/setFilteredTagIds`)

const addFilteredTagIds = createAction<{ level: number; id?: string } | undefined>(
    `${name}/addTagFilterIds`
)

const resetWorkspaceTree = createAction(`${name}/resetWorkspaceTree`)

const setSearchContents = createAction<string | undefined>(`${name}/setSearchContents`)

const setIsSearchingGlobally = createAction<boolean>(`${name}/setIsSearchingGlobally`)

const setIdRenaming = createAction<ID | undefined>(`${name}/setIdRenaming`)

const setActivatingProjectId = createAction<ID | undefined>(`${name}/setActivatingProjectId`)

const setActiveFlyoutMenuId = createAction<ID | undefined>(`${name}/setActiveFlyoutMenuId`)

const setDropdownOpen = createAction<{ id: ID; isOpen: boolean }>(`${name}/setDropdownOpen`)

const setIsCreatingProject = createAction<boolean>(`${name}/setIsCreatingProject`)

const setNewestProjectId = createAction<ID>(`${name}/setNewestProjecTId`)

const getAllWorkspaces = createAsyncThunk(`${name}/getAllWorkspaces`, () =>
    fetchApiGateway("get /orgs")
)

const fetchExtendedWorkspace = createAppAsyncThunk(
    `${name}/fetchExtendedWorkspace`,
    async (orgId: string, { rejectWithValue }) => {
        try {
            return await fetchApiGateway("get /org/:orgId", {
                params: { orgId },
                region: SupportedRegions.Canada, // Force to Canada because it needs to access Canadian redis. Projects are pulled from dynamo based on org.region
            })
        } catch (error) {
            return rejectWithValue(error)
        }
    }
)

const updateWorkspace = createAsyncThunk(
    `${name}/updateWorkspace`,
    ({
        id,
        updates,
    }: {
        id: ID
        updates: {
            name?: string
            location?: OrganizationExtended["location"]
            settings?: OrganizationExtended["settings"]
        }
    }) => fetchApiGateway("patch /org/:orgId", { params: { orgId: id }, body: updates })
)

const updateTag = createAppAsyncThunk(
    `${name}/updateTag`,
    async (
        {
            id,
            updates,
        }: {
            id: ID
            updates: {
                name?: string
                parentId?: string
            }
        },
        { getState }
    ) => {
        const state = getState()
        const orgId = selectActiveWorkspaceId()(state)!
        return await fetchApiGateway("patch /org/:orgId/tag/:tagId", {
            params: { orgId, tagId: id },
            body: updates,
        })
    }
)
const createWorkspace = createAsyncThunk(
    `${name}/createWorkspace`,
    ({
        fullName,
        location,
        settings,
    }: {
        fullName: string
        settings: OrganizationExtended["settings"]
        location?: OrganizationExtended["location"]
    }) => fetchApiGateway("post /orgs", { body: { fullName, location, settings } })
)
const createFolder = createAsyncThunk(
    `${name}/createFolder`,
    async ({
        folderName,
        activeWorkspaceId,
        filteredTagIds,
    }: {
        folderName: string
        activeWorkspaceId: string
        filteredTagIds?: ID[]
    }) =>
        await fetchApiGateway("post /org/:orgId/tag", {
            params: { orgId: activeWorkspaceId },
            body: { name: folderName, parentId: filteredTagIds?.at(-1) },
        })
)

const deleteWorkspace = createAsyncThunk(`${name}/deleteWorkspace`, (id: ID) =>
    fetchApiGateway("delete /org/:orgId", { params: { orgId: id } })
)
const deleteTag = createAsyncThunk(
    `${name}/deleteTag`,
    async ({ id, activeWorkspaceId }: { id: ID; activeWorkspaceId: string }) =>
        await fetchApiGateway("delete /org/:orgId/tag/:tagId", {
            params: { orgId: activeWorkspaceId, tagId: id },
        })
)

const slice = createSlice({
    name,
    initialState: {
        workspaceTree: [],
        workspaces: [],
        tags: [],
        tagIds: [],
        filteredTagIds: [],
        isSearchingGlobally: false,
        dropdownsOpen: {},
        isCreatingProject: false,
    } as SliceState,
    reducers: {},
    extraReducers: (builder) =>
        builder
            .addCase(getAllWorkspaces.fulfilled, (state, action) => {
                const { organizations } = action.payload
                state.workspaces = organizations
            })
            .addCase(fetchExtendedWorkspace.pending, (state) => {
                state.activeWorkspace = undefined
            })
            .addCase(fetchExtendedWorkspace.fulfilled, (state, action) => {
                state.activeWorkspace = action.payload.organization
                const region = action.payload.organization.region
                sessionStorage.setItem("region", JSON.stringify(region || SupportedRegions.Canada))
                state.tags = flattenTags(state.activeWorkspace?.tags || [])
            })
            .addCase(updateWorkspace.fulfilled, (state, action) => {
                const id = action.meta.arg.id
                const updatedWorkspace = action.payload
                const workspaceIds = state.workspaces.map((workspace) => workspace.id)
                const index = workspaceIds.indexOf(id)
                const oldWorkspace = state.workspaces[index]
                const newWorkspace = { ...oldWorkspace, ...updatedWorkspace }
                state.workspaces[index] = newWorkspace
                state.activeWorkspace = { ...state.activeWorkspace!, ...updatedWorkspace }
            })
            .addCase(updateTag.fulfilled, (state, action) => {
                const { tag: updatedTag, newTagTree } = action.payload
                const id = action.meta.arg.id
                const tagIds = state.tags.map((tag) => tag.id)
                const index = tagIds.indexOf(id)
                const oldTag = state.tags[index]
                state.tags[index] = { ...oldTag, ...updatedTag }
                state.activeTag = { ...oldTag, ...updatedTag }

                const workspace = state.activeWorkspace
                if (!workspace || !newTagTree) return

                workspace.tagTree = newTagTree
                workspace.tags = buildNestedTagTree(newTagTree, state.tags)
                state.tags = flattenTags(workspace.tags)
                const basicOrg = state.workspaces.find(({ id }) => id === state.activeWorkspace?.id)
                if (basicOrg) basicOrg.tagTree = newTagTree
            })
            .addCase(createWorkspace.fulfilled, (state, action) => {
                const newWorkspace = action.payload.organization
                state.workspaces = [...state.workspaces, newWorkspace]
            })
            .addCase(createFolder.fulfilled, (state, action) => {
                const payload = action.payload
                const newTagTree = payload.newTagTree
                const newTag = payload.tag

                state.tags.push(newTag)

                if (!state.activeWorkspace) {
                    return
                }
                state.activeWorkspace.tagTree = newTagTree
                state.activeWorkspace.tags = buildNestedTagTree(newTagTree, state.tags)
                state.tags = flattenTags(state.activeWorkspace.tags)
                const basicOrg = state.workspaces.find(({ id }) => id === state.activeWorkspace?.id)
                if (basicOrg) basicOrg.tagTree = newTagTree

                state.newestTagId = newTag.id

                state.idRenaming = `${getLocalStorageValue("contentViewMode") === ContentViewMode.Grid ? "grid" : "list"}_${newTag.id}`
            })
            .addCase(setActiveTagAction, (state, action) => {
                const tag = state.tags.find((tag) => tag.id === action.payload)
                if (!tag) {
                    state.activeTag = undefined
                } else {
                    state.activeTag = tag
                }
            })
            .addCase(addFilteredTagIds, (state, action) => {
                if (!action.payload) {
                    state.filteredTagIds = []
                    return
                }
                const { level, id } = action.payload
                const newFilters = state.filteredTagIds.slice(0, level)
                if (id) {
                    newFilters[level] = id
                }
                state.filteredTagIds = newFilters
            })
            .addCase(setFilteredTagIds, (state, action) => {
                const filteredTags = action.payload
                if (!filteredTags) {
                    state.filteredTagIds = []
                    return
                }
                state.filteredTagIds = filteredTags
            })
            .addCase(deleteWorkspace.fulfilled, (state, action) => {
                const workspaceId = action.meta.arg
                const workspaceIndex = state.workspaces
                    .map((workspace) => workspace.id)
                    .indexOf(workspaceId)
                state.workspaces.splice(workspaceIndex, 1)

                state.activeWorkspace = undefined
            })
            .addCase(deleteTag.fulfilled, (state, action) => {
                const tagId = action.meta.arg.id
                const { newTagTree } = action.payload
                const tagIndex = state.tags.map((tag) => tag.id).indexOf(tagId)
                state.tags.splice(tagIndex, 1)

                if (!state.activeWorkspace) {
                    return
                }
                state.activeWorkspace.tagTree = newTagTree
                state.activeWorkspace.tags = buildNestedTagTree(newTagTree, state.tags)
                state.tags = flattenTags(state.activeWorkspace.tags)
                const basicOrg = state.workspaces.find(({ id }) => id === state.activeWorkspace?.id)
                if (basicOrg) basicOrg.tagTree = newTagTree
            })
            .addCase(resetWorkspaceTree, (state) => {
                state.activeWorkspace = undefined
                state.activeTag = undefined
                state.filteredTagIds = []
            })
            .addCase(setSearchContents, (state, action) => {
                state.searchContents = action.payload
            })
            .addCase(setIsSearchingGlobally, (state, action) => {
                state.isSearchingGlobally = action.payload
            })

            .addCase(setIdRenaming, (state, action) => {
                state.idRenaming = action.payload
                if (!action.payload) {
                    state.newestProjectId = undefined
                    state.newestTagId = undefined
                }
            })
            .addCase(setActiveFlyoutMenuId, (state, action) => {
                state.activeFlyoutMenuId = action.payload
            })
            .addCase(setDropdownOpen, (state, action) => {
                state.dropdownsOpen[action.payload.id] = action.payload.isOpen
            })
            .addCase(setIsCreatingProject, (state, action) => {
                state.isCreatingProject = action.payload
            })
            .addCase(setNewestProjectId, (state, action) => {
                state.newestProjectId = action.payload
            })
            .addCase(setActivatingProjectId, (state, action) => {
                state.activatingProjectId = action.payload
            })
            .addCase(createFire.fulfilled, (state, action) => {
                if (action.payload.tagId) {
                    state.tags
                        .find((e) => e.id === action.payload.tagId)
                        ?.projects.push(action.payload)
                } else {
                    state.activeWorkspace!.rootLevelProjects.push(action.payload)
                }
            })
            .addCase(updateFire.fulfilled, (state, action) => {
                const { id, updates } = action.meta.arg
                if (updates.tagId === undefined) return

                const newTagId = updates.tagId
                let movedProject:
                    | NonNullable<typeof state.activeWorkspace>["rootLevelProjects"][0]
                    | undefined

                for (const tag of state.tags) {
                    const idx = tag.projects.findIndex((p) => p.id === id)
                    if (idx > -1) {
                        movedProject = tag.projects[idx]
                        tag.projects.splice(idx, 1)
                        break
                    }
                }

                if (!movedProject && state.activeWorkspace) {
                    const idx = state.activeWorkspace.rootLevelProjects.findIndex(
                        (p) => p.id === id
                    )
                    if (idx > -1) {
                        movedProject = state.activeWorkspace.rootLevelProjects[idx]
                        state.activeWorkspace.rootLevelProjects.splice(idx, 1)
                    }
                }

                if (!movedProject) return

                if (newTagId === "root") {
                    state.activeWorkspace?.rootLevelProjects.push(movedProject)
                } else {
                    state.tags.find((t) => t.id === newTagId)?.projects.push(movedProject)
                }
            })
            .addCase(loadAndZoomProject.pending, (state, action) => {
                state.activatingProjectId = action.meta.arg.projectId
            })
            .addCase(loadAndZoomProject.fulfilled, (state) => {
                state.activatingProjectId = undefined
            }),
})

export {
    getAllWorkspaces,
    fetchExtendedWorkspace,
    updateWorkspace,
    updateTag,
    setActiveTagAction,
    setFilteredTagIds,
    addFilteredTagIds,
    createWorkspace,
    createFolder,
    deleteWorkspace,
    deleteTag,
    resetWorkspaceTree,
    setSearchContents,
    setIsSearchingGlobally,
    setIdRenaming,
    setActivatingProjectId,
    setActiveFlyoutMenuId,
    setDropdownOpen,
    setIsCreatingProject,
    setNewestProjectId,
}

export const reducer = { [name]: slice.reducer }
export { selectFilteredTags, selectTagById }

const flattenTags = (tags: TagExtended[]): TagExtended[] =>
    tags.flatMap((tag) => [tag, ...flattenTags(tag.subTags)])

const buildNestedTagTree = (tagTree: TagTree, tags: TagExtended[]): TagExtended[] => {
    const row: TagExtended[] = []
    for (const [key, value] of Object.entries(tagTree)) {
        if (!value) continue
        const tag = tags.find((t) => t.id === key)
        if (!tag) continue
        if (Object.keys(value).length === 0) {
            row.push({ ...tag, subTags: [] })
            continue
        }
        row.push({ ...tag, subTags: buildNestedTagTree(value, tags) })
    }
    return row
}

export const selectWorkspaceDistanceUnit = createSelector(
    [selectActiveWorkspace()],
    (workspace) =>
        (workspace?.settings?.find((setting) => setting.name === "distance")
            ?.value as DistanceUnit) || DistanceUnit.Feet
)

export const selectWorkspaceAreaUnit = createSelector(
    [selectActiveWorkspace()],
    (workspace) =>
        (workspace?.settings?.find((setting) => setting.name === "area")?.value as AreaUnit) ||
        AreaUnit.Acres
)

export const selectWorkspaceUnitSystem = createSelector(
    [selectActiveWorkspace()],
    (workspace): "metric" | "imperial" => {
        const distance = workspace?.settings?.find((setting) => setting.name === "distance")?.value
        if (distance === DistanceUnit.Meters) return "metric"
        if (distance === DistanceUnit.Feet) return "imperial"
        return workspace?.region === SupportedRegions.USA ? "imperial" : "metric"
    }
)

export const selectWorkspaceDateFormat = createSelector(
    [selectActiveWorkspace()],
    (workspace) =>
        (workspace?.settings?.find((setting) => setting.name === "date")?.value as DateFormat) ||
        DateFormat.YearMonthDay
)

export const selectWorkspaceTimeFormat = createSelector(
    [selectActiveWorkspace()],
    (workspace) =>
        (workspace?.settings?.find((setting) => setting.name === "time")?.value as TimeFormat) ||
        TimeFormat.TwentyFourHour
)

export const selectDefaultChecklistId = createSelector(
    [selectActiveWorkspace()],
    (workspace) =>
        (workspace?.settings?.find((s) => s.name === "default_checklist_id")?.value as string) ||
        null
)
